import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Plural, Trans } from "@lingui/react/macro"
import { ActionIcon, Button, Center, Stack, Tooltip } from "@mantine/core"
import { IconChecks, IconRotateClockwise, IconSparkles, IconX } from "@tabler/icons-react"
import { useState } from "react"
import { EmptyState } from "../../../../components/EmptyState"
import { JargonSuggestionList } from "../../../../components/JargonSuggestionList"
import { LineListSkeleton } from "../../../../components/PageSkeletons"
import { displayMeetingTitle } from "../../../../lib/meeting-title"
import { groupJargonByMeeting, type IJargonMeetingGroup } from "../group"
import { JargonHistoryRow } from "../JargonHistoryRow"
import { JargonMeetingGroup } from "../JargonMeetingGroup"
import type { IProps } from "./interfaces"
import classes from "./styles.module.css"

/** Are every / some of a group's suggestions in the selected set? */
function groupSelectionState(
  group: IJargonMeetingGroup,
  selectedIds: ReadonlySet<string>,
): { all: boolean; some: boolean; ids: string[] } {
  const ids = group.suggestions.map((s) => s.id)
  const selectedCount = ids.reduce((n, id) => (selectedIds.has(id) ? n + 1 : n), 0)
  return { all: selectedCount === ids.length && ids.length > 0, some: selectedCount > 0, ids }
}

const SEEN_ONCE_PANEL_ID = "jargon-seen-once"

/**
 * JargonManagerList renders the review queue (pending) or a decided-history view
 * (accepted / dismissed), grouped under per-meeting headers. Dumb component: the
 * host owns the data, selection set, and mutations; this only renders the
 * skeleton / empty state / groups and reports interactions. Review groups reuse
 * the shared JargonSuggestionList rows (with selection); history groups render
 * read-only rows with a restore action. One-meeting terms older than 14 days
 * sit in a collapsed group the host already partitioned.
 */
export function JargonManagerList({
  mode,
  groups,
  seenOnce = [],
  isLoading,
  selectedIds,
  onToggleSelect,
  onToggleGroup,
  busyId = null,
  exitingIds,
  onAccept,
  onDismiss,
  onRestore,
  onBulkGroup,
  total,
  loadedCount,
  onLoadMore,
  isLoadingMore,
}: IProps): React.JSX.Element {
  const [seenOnceOpen, setSeenOnceOpen] = useState(false)
  const { i18n } = useLingui()
  const folded = mode === "review" ? seenOnce : []

  /** Per-group header shortcut — accept/dismiss (review) or restore (history) a whole meeting. */
  const groupActions = (subject: string, ids: string[]): React.JSX.Element | undefined => {
    if (!onBulkGroup) return undefined
    const n = ids.length
    return mode === "review" ? (
      <>
        <Tooltip label={i18n._(msg`Add all ${n} from ${subject} to dictionary`)} withArrow>
          <ActionIcon
            variant="subtle"
            color="success"
            size="sm"
            onClick={() => onBulkGroup(ids, "accept")}
            aria-label={i18n._(msg`Add all ${n} from ${subject} to dictionary`)}
          >
            <IconChecks size={16} />
          </ActionIcon>
        </Tooltip>
        <Tooltip label={i18n._(msg`Dismiss all ${n} from ${subject}`)} withArrow>
          <ActionIcon
            variant="subtle"
            color="danger"
            size="sm"
            onClick={() => onBulkGroup(ids, "dismiss")}
            aria-label={i18n._(msg`Dismiss all ${n} from ${subject}`)}
          >
            <IconX size={16} />
          </ActionIcon>
        </Tooltip>
      </>
    ) : (
      <Tooltip label={i18n._(msg`Restore all ${n} from ${subject} to review`)} withArrow>
        <ActionIcon
          variant="subtle"
          color="brand"
          size="sm"
          onClick={() => onBulkGroup(ids, "restore")}
          aria-label={i18n._(msg`Restore all ${n} from ${subject} to review`)}
        >
          <IconRotateClockwise size={16} />
        </ActionIcon>
      </Tooltip>
    )
  }

  const renderGroups = (meetingGroups: IJargonMeetingGroup[]): React.JSX.Element[] =>
    meetingGroups.map((group) => {
      const { all, some, ids } = groupSelectionState(group, selectedIds)
      const groupLabel =
        group.subject === null ? i18n._(msg`this meeting`) : displayMeetingTitle(group.subject, null, i18n)
      return (
        <JargonMeetingGroup
          key={group.meetingId}
          subject={group.subject}
          count={group.suggestions.length}
          selectable
          allSelected={all}
          someSelected={some}
          onToggleAll={() => onToggleGroup(ids, !all)}
          headerActions={groupActions(groupLabel, ids)}
        >
          {mode === "review" ? (
            <JargonSuggestionList
              variant="panel"
              suggestions={group.suggestions}
              busyId={busyId}
              selectedIds={selectedIds}
              exitingIds={exitingIds}
              onToggleSelect={onToggleSelect}
              onAccept={(id, editedTerm) => onAccept?.(id, editedTerm)}
              onDismiss={(id) => onDismiss?.(id)}
            />
          ) : (
            group.suggestions.map((s) => (
              <JargonHistoryRow
                key={s.id}
                suggestion={s}
                selected={selectedIds.has(s.id)}
                busy={busyId === s.id}
                exiting={exitingIds?.has(s.id) ?? false}
                onToggleSelect={onToggleSelect}
                onRestore={(id) => onRestore?.(id)}
              />
            ))
          )}
        </JargonMeetingGroup>
      )
    })

  if (isLoading) {
    return <LineListSkeleton rows={5} />
  }

  if (groups.length === 0 && folded.length === 0) {
    return mode === "review" ? (
      <EmptyState
        icon={<IconSparkles size={22} />}
        tone="brand"
        title={i18n._(msg`Nothing to review`)}
        description={i18n._(msg`Terms detected in processed meetings show up here for review.`)}
      />
    ) : (
      <EmptyState
        icon={<IconChecks size={22} />}
        tone="neutral"
        title={i18n._(msg`No decisions yet`)}
        description={i18n._(msg`Once you accept or dismiss suggestions, they're kept here.`)}
      />
    )
  }

  const showHint = mode === "review" && (groups.length > 0 || folded.length > 0)

  return (
    <Stack gap="lg">
      {showHint && (
        <p className={classes.hint}>
          <Trans>Fix the spelling before you add a word.</Trans>
        </p>
      )}
      {renderGroups(groups)}
      {folded.length > 0 && (
        <section className={classes.seenOnce}>
          <button
            type="button"
            className={classes.seenOnceToggle}
            aria-expanded={seenOnceOpen}
            aria-controls={SEEN_ONCE_PANEL_ID}
            onClick={() => setSeenOnceOpen((open) => !open)}
          >
            <Trans>Seen once</Trans>
            <span className={classes.seenOnceCount}>
              <Plural value={folded.length} one="# term" other="# terms" />
            </span>
          </button>
          {seenOnceOpen && (
            <Stack id={SEEN_ONCE_PANEL_ID} gap="lg">
              {renderGroups(groupJargonByMeeting(folded))}
            </Stack>
          )}
        </section>
      )}

      {loadedCount < total && (
        <Center>
          <Button variant="default" onClick={onLoadMore} loading={isLoadingMore}>
            <Trans>Show more ({total - loadedCount})</Trans>
          </Button>
        </Center>
      )}
    </Stack>
  )
}
